import React, { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { VisitsChartData, TimePeriod } from '@/types/dashboard.types';

interface VisitsChartProps {
  data: VisitsChartData[];
  isLoading?: boolean;
  onPeriodChange?: (period: TimePeriod) => void;
}

const periodOptions: { value: TimePeriod; label: string }[] = [
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
];

const LoadingSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="flex items-center justify-between mb-6">
      <div className="h-6 bg-secondary-200 rounded w-32"></div>
      <div className="h-8 bg-secondary-100 rounded w-28"></div>
    </div>
    <div className="h-64 bg-secondary-100 rounded-lg"></div>
  </div>
);

export const VisitsChart: React.FC<VisitsChartProps> = ({
  data,
  isLoading = false,
  onPeriodChange,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('month');

  const handlePeriodChange = (period: TimePeriod) => {
    setSelectedPeriod(period);
    onPeriodChange?.(period);
  };

  if (isLoading) {
    return (
      <Card className="h-full">
        <LoadingSkeleton />
      </Card>
    );
  }

  // Calculate max value for scaling
  const maxValue = Math.max(
    ...data.map((d) => Math.max(d.scheduled, d.completed, d.no_shows)),
    1
  );
  const chartHeight = 200;

  // Calculate totals
  const totalScheduled = data.reduce((sum, d) => sum + d.scheduled, 0);
  const totalCompleted = data.reduce((sum, d) => sum + d.completed, 0);
  const totalNoShows = data.reduce((sum, d) => sum + d.no_shows, 0);
  const completionRate = totalScheduled > 0
    ? Math.round((totalCompleted / totalScheduled) * 100)
    : 0;

  return (
    <Card className="h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-secondary-900">Visits Overview</h3>
          <p className="text-sm text-secondary-500 mt-1">
            {completionRate}% completion rate
          </p>
        </div>
        <select
          value={selectedPeriod}
          onChange={(e) => handlePeriodChange(e.target.value as TimePeriod)}
          className="text-sm border border-secondary-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {periodOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-3 bg-blue-50 rounded-lg">
          <p className="text-2xl font-bold text-blue-600">{totalScheduled}</p>
          <p className="text-xs text-blue-600 mt-1">Scheduled</p>
        </div>
        <div className="text-center p-3 bg-green-50 rounded-lg">
          <p className="text-2xl font-bold text-green-600">{totalCompleted}</p>
          <p className="text-xs text-green-600 mt-1">Completed</p>
        </div>
        <div className="text-center p-3 bg-red-50 rounded-lg">
          <p className="text-2xl font-bold text-red-600">{totalNoShows}</p>
          <p className="text-xs text-red-600 mt-1">No Shows</p>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
          <span className="text-sm text-secondary-600">Scheduled</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-sm text-secondary-600">Completed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <span className="text-sm text-secondary-600">No Shows</span>
        </div>
      </div>

      {/* Chart */}
      {data.length === 0 ? (
        <div className="flex items-center justify-center h-40 bg-secondary-50 rounded-lg">
          <p className="text-secondary-500">No data available</p>
        </div>
      ) : (
        <div className="relative">
          {/* Bars */}
          <div
            className="flex items-end justify-around gap-1"
            style={{ height: chartHeight }}
          >
            {data.map((item, index) => {
              const scheduledHeight = (item.scheduled / maxValue) * 100;
              const completedHeight = (item.completed / maxValue) * 100;
              const noShowsHeight = (item.no_shows / maxValue) * 100;

              return (
                <div
                  key={index}
                  className="flex-1 flex flex-col items-center group max-w-20"
                >
                  <div className="relative w-full flex justify-center gap-0.5 h-full items-end">
                    {/* Scheduled bar */}
                    <div
                      className="w-4 bg-blue-500 rounded-t-sm transition-all duration-300 hover:bg-blue-600"
                      style={{ height: `${scheduledHeight}%`, minHeight: item.scheduled > 0 ? '4px' : '0' }}
                      title={`Scheduled: ${item.scheduled}`}
                    ></div>
                    {/* Completed bar */}
                    <div
                      className="w-4 bg-green-500 rounded-t-sm transition-all duration-300 hover:bg-green-600"
                      style={{ height: `${completedHeight}%`, minHeight: item.completed > 0 ? '4px' : '0' }}
                      title={`Completed: ${item.completed}`}
                    ></div>
                    {/* No Shows bar */}
                    <div
                      className="w-4 bg-red-500 rounded-t-sm transition-all duration-300 hover:bg-red-600"
                      style={{ height: `${noShowsHeight}%`, minHeight: item.no_shows > 0 ? '4px' : '0' }}
                      title={`No Shows: ${item.no_shows}`}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* X-axis labels */}
          <div className="flex justify-around mt-3">
            {data.map((item, index) => (
              <span
                key={index}
                className="text-xs text-secondary-500 text-center flex-1 truncate px-0.5"
              >
                {item.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export default VisitsChart;
